import {
	Opportunity,
	Parcel,
	ParcelSchema,
	Prospect,
	ProspectSchema,
	DevelopmentPotential,
	Transaction,
	TransactionSchema,
	Region,
	Municipality,
	MunicipalitySchema,
	BoroughRecord,
	BoroughRecordSchema,
	Contact,
	ContactList,
	CompanyList,
	ContactListSchema,
	CompanyListSchema,
	UrbanPriority,
	BuyersList,
	type Project,
} from "@/model/DataModel.ts"
import * as Sentry from "@sentry/browser"
import axios from "redaxios"
import type { Filters } from "@/model/RootStore"
import type {
	ActivityType,
	Page,
	Unit,
	OpportunityPriority,
	OpportunityStage,
	OpportunityTarget,
	OpportunityType,
	NotificationTopic,
	ProjectInput,
	ProjectSearchContextInput,
	SearchContext,
	CreateSearchContextInput,
	UpdateSearchContextInput,
	UpdateOpportunityInput,
	ViewInput,
	Zone,
} from "@/graphql"
import { CLIENT_ONLY_VIEW_KEYS, type TransactionVerificationStatus, type View } from "@/types"
import {
	ContactResearchEventType,
	ContactResearchProviderStatus,
	type ContactResearchEvent,
	type ContactResearchInput,
	type ContactResearchProviderId,
	type ContactResearchProviderInfo,
	type ContactResearchResult,
} from "@/types/contactResearch"
import { deepToRaw } from "@/utils"
import type { ExtendedAccess } from "@/types/extendedAccess"
import type { ParcelReportContent } from "@/types/parcelReport"
import type { RegulatoryAssistantResponse } from "@/types/regulatoryAssistant"
import { mapOpportunityRead } from "./opportunityReadMapper"
import { mapRegulatoryAssistantResponse } from "./regulatoryAssistantMapper"

type ApiError = { message: string; extensions?: { code?: string } }

export function toApiViewInput(view: View): ViewInput {
	const input = structuredClone(deepToRaw(view)) as any
	for (const key of CLIENT_ONLY_VIEW_KEYS) delete input[key]
	normalizeApiDateRange(input.filter?.opportunity?.launchDate)
	normalizeApiDateRange(input.filter?.transaction?.date)
	normalizeApiDateRange(input.filter?.buyer?.transaction?.date)

	return input as ViewInput
}

function normalizeApiDateRange(range: unknown): void {
	if (!range || typeof range !== "object" || Array.isArray(range)) return

	const dateRange = range as { min?: unknown; max?: unknown }
	dateRange.min = apiDateValue(dateRange.min)
	dateRange.max = apiDateValue(dateRange.max)
}

function apiDateValue(value: unknown): string | null {
	if (value == null) return null
	if (value instanceof Date) return value.toISOString().split("T")[0]
	if (typeof value === "string") return value
	return null
}

const OPPORTUNITY_FIELDS = `
	analysis_buildable_area
	analysis_buildable_area_price_per_sqft
	analysis_land_area
	analysis_land_area_price_per_sqft
	broker_email
	broker_first_name
	broker_last_name
	broker_phone_number
	broker_picture_url
	collaborative_broker_names
	collaborative_broker_1_name
	collaborative_broker_1_email
	collaborative_broker_1_phone
	collaborative_broker_1_picture
	collaborative_broker_2_name
	collaborative_broker_2_email
	collaborative_broker_2_phone
	collaborative_broker_2_picture
	call_for_bids
	development_type
	hubspot_record_id
	id
	latitude
	launch_date
	opportunity_asking_price
	opportunity_expiration_date
	opportunity_type
	opportunity_webpage_url
	longitude
	lot_address
	lot_image_url
	lot_postal_code
	lot_borough
	lot_region
	lot_state
	lot_city
	off_market_radius
	sales_process_status
	lot_numbers
	lot_numbers_full
	all_owners_ids
	all_owners_names
	primary_seller
	priority
	project_status
	status
	information_on_existing_conditions
	information_on_potential_and_zoning
	owner_information
	data_room_cloud_file
	modelling_link
	marketing_type
	stage_label
	isSyncPending
	isPlaceholder
	assetType
	landCategoryNew
	landSubcategory
	priceExpectation
	pricingSource
	residentialUnits
	pricePerUnit
	ownerTypes
	ownershipDuration
	motivationsToSell
	potentialDealStructures
	mainZoning
	projectApprovalProcesses
	municipalReceptiveness
	lastConversationCity
	knownDevelopmentRestrictions
	municipalInfrastructures
	lastConversationOwner
	commentsAboutOwner
`

function urljoin(...parts: Array<string | URL>): URL {
	const sanitized = parts.map(p => {
		p = p.toString()
		p = p.trim()
		p = p.replace(/\/$/, "")
		p = p.replace(/^\//, "")

		return p
	})
	return new URL(sanitized.join("/"))
}

function contactResearchUrl(path: string): URL {
	const apiUrl = new URL(import.meta.env.VITE_GRAPHQL_API_ENDPOINT)
	const baseUrl = new URL("/contact-research/", apiUrl)

	return new URL(path, baseUrl)
}

const CONTACT_RESEARCH_EVENT_POLL_INTERVAL_MS = 2_000

async function contactResearchResponseError(response: Response): Promise<Error> {
	const body = (await response.json().catch(() => null)) as {
		error?: string | { message?: string }
		message?: string
	} | null
	const error = body?.error
	const message = typeof error === "string" ? error : error?.message || body?.message

	return new Error(message || `Contact research request failed with status ${response.status}`)
}

function processContactResearchEvent(
	event: ContactResearchEvent,
	onEvent: (event: ContactResearchEvent) => void
): boolean {
	if (
		event.type === ContactResearchEventType.ProviderCompleted &&
		!Object.values(ContactResearchProviderStatus).includes(event.result.status)
	) {
		throw new Error(`Unknown contact research provider status: ${event.result.status}`)
	}

	onEvent(event)

	return event.type === ContactResearchEventType.RunCompleted || event.type === ContactResearchEventType.RunFailed
}

function waitForContactResearchEvents(signal?: AbortSignal): Promise<void> {
	return new Promise(resolve => {
		const timer = setTimeout(resolve, CONTACT_RESEARCH_EVENT_POLL_INTERVAL_MS)
		signal?.addEventListener(
			"abort",
			() => {
				clearTimeout(timer)
				resolve()
			},
			{ once: true }
		)
	})
}

export async function getContactResearchProviders(apiToken: string): Promise<ContactResearchProviderInfo[]> {
	const response = await fetch(contactResearchUrl("providers"), {
		headers: { "x-api-token": apiToken },
	})

	if (!response.ok) {
		throw await contactResearchResponseError(response)
	}

	const body = (await response.json()) as { providers: ContactResearchProviderInfo[] }

	return body.providers
}

export async function startContactResearchRun(
	apiToken: string,
	input: ContactResearchInput,
	providers: ContactResearchProviderId[]
): Promise<{ runId: string; providers: ContactResearchProviderInfo[] }> {
	const response = await fetch(contactResearchUrl("runs"), {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"x-api-token": apiToken,
		},
		body: JSON.stringify({ input, providers }),
	})

	if (!response.ok) {
		throw await contactResearchResponseError(response)
	}

	return (await response.json()) as { runId: string; providers: ContactResearchProviderInfo[] }
}

export async function getContactResearchRun(
	apiToken: string,
	runId: string
): Promise<{ result: ContactResearchResult | null; error: string | null }> {
	const response = await fetch(contactResearchUrl(`runs/${encodeURIComponent(runId)}`), {
		headers: { "x-api-token": apiToken },
	})

	if (!response.ok) {
		throw await contactResearchResponseError(response)
	}

	return (await response.json()) as { result: ContactResearchResult | null; error: string | null }
}

export async function streamContactResearchEvents(
	apiToken: string,
	runId: string,
	onEvent: (event: ContactResearchEvent) => void,
	signal?: AbortSignal
): Promise<void> {
	let afterEventId = 0

	while (!signal?.aborted) {
		const url = contactResearchUrl(`runs/${encodeURIComponent(runId)}`)
		url.searchParams.set("afterEventId", String(afterEventId))
		const response = await fetch(url, {
			headers: {
				Accept: "application/json",
				"x-api-token": apiToken,
			},
			signal,
		})

		if (!response.ok) {
			throw await contactResearchResponseError(response)
		}

		const snapshot = (await response.json()) as {
			events?: Array<{ id: number; event: ContactResearchEvent }>
			completed?: boolean
			result?: ContactResearchResult | null
			error?: string | null
		}

		for (const entry of snapshot.events ?? []) {
			afterEventId = Math.max(afterEventId, entry.id)

			if (processContactResearchEvent(entry.event, onEvent)) {
				return
			}
		}

		if (snapshot.completed) {
			return
		}

		if (snapshot.result) {
			onEvent({
				type: ContactResearchEventType.RunCompleted,
				runId,
				result: snapshot.result,
				timestamp: snapshot.result.completedAt,
			})

			return
		}

		if (snapshot.error) {
			onEvent({
				type: ContactResearchEventType.RunFailed,
				runId,
				error: snapshot.error,
				timestamp: new Date().toISOString(),
			})

			return
		}

		if (!snapshot.events) {
			await waitForContactResearchEvents(signal)
		}
	}
}

function processOpportunityData(opportunityItem: any): Opportunity {
	opportunityItem = mapOpportunityRead(opportunityItem)

	let newOpportunity: Opportunity = {
		id: opportunityItem.id,
		hubspot_record_id: opportunityItem.hubspot_record_id,
		type: opportunityItem.opportunity_type,
		typeDev: opportunityItem.development_type || [],
		superficy: opportunityItem.analysis_land_area || 0,
		latitude: opportunityItem.latitude,
		longitude: opportunityItem.longitude,
		constructible: opportunityItem.analysis_buildable_area || 0,
		price: opportunityItem.opportunity_asking_price || 0,
		pricePerSqft: opportunityItem.analysis_land_area_price_per_sqft ?? null,
		pricePerConstructibleSqft: opportunityItem.analysis_buildable_area_price_per_sqft ?? null,
		link: opportunityItem.opportunity_webpage_url,
		address: opportunityItem.lot_address,
		city: opportunityItem.lot_city,
		state: opportunityItem.lot_state,
		region: opportunityItem.lot_region ?? null,
		borough: opportunityItem.lot_borough ?? null,
		postal_code: opportunityItem.lot_postal_code,
		image: opportunityItem.lot_image_url,
		off_market_radius: Number(opportunityItem.off_market_radius),
		sales_process_status: opportunityItem.sales_process_status,
		launch_date: undefined,
		launch_date_ms: undefined,
		call_for_bids: Boolean(opportunityItem.call_for_bids),
		broker_email: opportunityItem.broker_email,
		broker_first_name: opportunityItem.broker_first_name,
		broker_last_name: opportunityItem.broker_last_name,
		broker_phone_number: opportunityItem.broker_phone_number,
		broker_picture_url: opportunityItem.broker_picture_url,
		collaborative_broker_names: opportunityItem.collaborative_broker_names,
		collaborative_broker_1_name: opportunityItem.collaborative_broker_1_name,
		collaborative_broker_1_email: opportunityItem.collaborative_broker_1_email,
		collaborative_broker_1_phone: opportunityItem.collaborative_broker_1_phone,
		collaborative_broker_1_picture: opportunityItem.collaborative_broker_1_picture,
		collaborative_broker_2_name: opportunityItem.collaborative_broker_2_name,
		collaborative_broker_2_email: opportunityItem.collaborative_broker_2_email,
		collaborative_broker_2_phone: opportunityItem.collaborative_broker_2_phone,
		collaborative_broker_2_picture: opportunityItem.collaborative_broker_2_picture,
		primary_seller: opportunityItem.primary_seller,
		lot_numbers_full: opportunityItem.lot_numbers_full,
		all_owners_names: opportunityItem.all_owners_names,
		project_status: opportunityItem.project_status || "",
		information_on_existing_conditions: opportunityItem.information_on_existing_conditions || "",
		information_on_potential_and_zoning: opportunityItem.information_on_potential_and_zoning || "",
		owner_information: opportunityItem.owner_information || "",
		data_room_cloud_file: opportunityItem.data_room_cloud_file || "",
		modelling_link: opportunityItem.modelling_link || "",
		status: opportunityItem.status,
		priority: opportunityItem.priority,
		marketing_type: opportunityItem.marketing_type,
		stageLabel: opportunityItem.stage_label || "",
		parent_opportunity_type: opportunityItem.parent_opportunity_type || "",
		isSyncPending: opportunityItem.isSyncPending ?? null,
		isPlaceholder: opportunityItem.isPlaceholder ?? null,
		assetType: opportunityItem.assetType,
		landCategoryNew: opportunityItem.landCategoryNew,
		landSubcategory: opportunityItem.landSubcategory,
		priceExpectation: opportunityItem.priceExpectation ?? null,
		pricingSource: opportunityItem.pricingSource ?? null,
		residentialUnits: opportunityItem.residentialUnits ?? null,
		pricePerUnit: opportunityItem.pricePerUnit ?? null,
		ownerTypes: opportunityItem.ownerTypes ?? null,
		ownershipDuration: opportunityItem.ownershipDuration ?? null,
		motivationsToSell: opportunityItem.motivationsToSell ?? null,
		potentialDealStructures: opportunityItem.potentialDealStructures ?? null,
		mainZoning: opportunityItem.mainZoning ?? null,
		projectApprovalProcesses: opportunityItem.projectApprovalProcesses ?? null,
		municipalReceptiveness: opportunityItem.municipalReceptiveness ?? null,
		lastConversationCity: opportunityItem.lastConversationCity ?? null,
		knownDevelopmentRestrictions: opportunityItem.knownDevelopmentRestrictions ?? null,
		municipalInfrastructures: opportunityItem.municipalInfrastructures ?? null,
		lastConversationOwner: opportunityItem.lastConversationOwner ?? null,
		commentsAboutOwner: opportunityItem.commentsAboutOwner ?? null,
	}

	if (opportunityItem.launch_date) {
		newOpportunity.launch_date = new Date(opportunityItem.launch_date)
		newOpportunity.launch_date_ms = newOpportunity.launch_date.getTime()
	} else {
		newOpportunity.launch_date_ms = 0
	}

	if (Number.isNaN(newOpportunity.pricePerSqft)) {
		newOpportunity.pricePerSqft = null
	}

	if (Number.isNaN(newOpportunity.pricePerConstructibleSqft)) {
		newOpportunity.pricePerConstructibleSqft = null
	}

	if (opportunityItem.lastConversationCity) {
		newOpportunity.lastConversationCity = new Date(opportunityItem.lastConversationCity)
	}

	if (opportunityItem.lastConversationOwner) {
		newOpportunity.lastConversationOwner = new Date(opportunityItem.lastConversationOwner)
	}

	return newOpportunity
}

function parseTransactionWithComponents(transactionData: Transaction): Transaction | null {
	const result = TransactionSchema.safeParse(transactionData)

	if (!result.success) {
		console.error("Invalid object data:", result.error.issues)
		return null
	}

	const components = transactionData.components
		?.map(component => parseTransactionWithComponents(component))
		.filter((component): component is Transaction => component != null)

	return {
		...result.data,
		components,
	}
}

export async function getOpportunitiesByView(
	apiToken: string,
	view: View,
	projectId?: string | null,
	limit?: number,
	offset?: number
): Promise<{ items: Array<Opportunity>; total: number }> {
	const viewInput = toApiViewInput(view)
	if (projectId) {
		return fetchProjectOpportunitiesByView(apiToken, projectId, viewInput, { limit, offset })
	}

	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post<{ data: { opportunities: { items: Opportunity[]; total: number } } }>(
		endpoint,
		{
			query: `query Opportunities($view: ViewInput!, $limit: Int, $offset: Int) {
				opportunities(view: $view, limit: $limit, offset: $offset) {
					total
					items {
						${OPPORTUNITY_FIELDS}
					}
				}
			}`,
			variables: {
				view: viewInput,
				limit,
				offset,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	try {
		const { items: rawOpportunities, total } = response.data.data.opportunities

		const mappedOpportunities: Opportunity[] = []

		for (let rawOpportunity of rawOpportunities) {
			mappedOpportunities.push(processOpportunityData(rawOpportunity))
		}

		return { items: mappedOpportunities, total }
	} catch (error: any) {
		throw new Error(error)
	}
}

export type OpportunityBrokerNameSource = {
	broker_first_name?: string | null
	broker_last_name?: string | null
	collaborative_broker_names?: string[] | null
}

export async function getOpportunityBrokerNameSourcesByView(
	apiToken: string,
	view: View,
	projectId?: string | null
): Promise<OpportunityBrokerNameSource[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const viewInput = toApiViewInput(view)
	const fields = `
		broker_first_name
		broker_last_name
		collaborative_broker_names
	`

	if (projectId) {
		const response = await axios.post<{
			data: { project: { opportunities: { items: OpportunityBrokerNameSource[] } } | null }
			errors?: ApiError[]
		}>(
			endpoint,
			{
				query: `query ProjectOpportunityBrokers($projectId: UUID!, $view: ViewInput!) {
					project(id: $projectId) {
						opportunities(view: $view) {
							items {
								${fields}
							}
						}
					}
				}`,
				variables: { projectId, view: viewInput },
			},
			{
				headers: {
					"x-api-token": apiToken,
				},
			}
		)

		throwGraphQLError(response, "Failed to fetch project opportunity brokers")

		return response.data.data.project?.opportunities.items ?? []
	}

	const response = await axios.post<{
		data: { opportunities: { items: OpportunityBrokerNameSource[] } }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `query OpportunityBrokers($view: ViewInput!) {
				opportunities(view: $view) {
					items {
						${fields}
					}
				}
			}`,
			variables: { view: viewInput },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwGraphQLError(response, "Failed to fetch opportunity brokers")

	return response.data.data.opportunities.items
}

async function fetchProjectOpportunitiesByView(
	apiToken: string,
	projectId: string,
	view: ViewInput,
	{
		limit,
		offset,
	}: {
		limit?: number
		offset?: number
	}
): Promise<{ items: Array<Opportunity>; total: number }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post<{
		data: { project: { opportunities: { items: Opportunity[]; total: number } } | null }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `query ProjectOpportunities($projectId: UUID!, $view: ViewInput!, $limit: Int, $offset: Int) {
				project(id: $projectId) {
					opportunities(view: $view, limit: $limit, offset: $offset) {
						total
						items {
							${OPPORTUNITY_FIELDS}
						}
					}
				}
			}`,
			variables: {
				projectId,
				view,
				limit,
				offset,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwGraphQLError(response, "Failed to fetch project opportunities")

	const page = response.data.data.project?.opportunities
	return {
		items: (page?.items ?? []).map(processOpportunityData),
		total: page?.total ?? 0,
	}
}

export async function getOpportunity(apiToken: string, opportunityId: string): Promise<Opportunity | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post<{ data?: { opportunity: Opportunity | null }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `query Opportunity($id: ID!) {
				opportunity(id: $id) {
					${OPPORTUNITY_FIELDS}
				}
			}`,
			variables: {
				id: opportunityId,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to fetch opportunity")
	}

	const opportunity = response.data.data?.opportunity
	return opportunity ? processOpportunityData(opportunity) : null
}

export async function getOpportunitiesByIds(
	apiToken: string,
	opportunityIds: string[],
	projectId?: string | null
): Promise<Opportunity[]> {
	if (opportunityIds.length === 0) {
		return []
	}

	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post<{
		data?: { opportunitiesByIds: Opportunity[] }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `query OpportunitiesByIds($ids: [ID!]!, $projectId: UUID) {
				opportunitiesByIds(ids: $ids, projectId: $projectId) {
					${OPPORTUNITY_FIELDS}
				}
			}`,
			variables: {
				ids: opportunityIds,
				projectId,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to fetch opportunities")
	}

	return (response.data.data?.opportunitiesByIds ?? []).map(processOpportunityData)
}

export async function getRegions(): Promise<Region[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	try {
		// prettier-ignore
		const response = await axios.post<{ data: { regions: Region[] } }>(
            endpoint,
            {
                query: `query GetRegions {
                    regions {
                        name
                    }
                }`
            }
        )

		// Data mapping
		return response.data.data.regions.map(region => ({
			name: region["name"],
		}))
	} catch (error: unknown) {
		throw new Error(`Error fetching regions: ${(error as Error).message}`)
	}
}

export async function getMunicipalities(): Promise<Municipality[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	try {
		// prettier-ignore
		const response = await axios.post<{ data: { municipalities: Municipality[] } }>(
            endpoint,
            {
                query: `query GetMunicipalities {
                    municipalities {
                        name
                        regionName
                    }
                }`
            }
        )

		// Data mapping
		const municipalities: Municipality[] = response.data.data.municipalities.map(municipality => ({
			name: municipality["name"],
			regionName: municipality["regionName"],
		}))

		// Validation
		const validMunicipalities = municipalities.filter(municipality => {
			const validated = MunicipalitySchema.safeParse(municipality)

			if (!validated.success) {
				console.error("Invalid object data:", validated.error.issues)
				return false
			}
			return true
		})

		return validMunicipalities
	} catch (error: unknown) {
		throw new Error(`Error fetching municipalities: ${(error as Error).message}`)
	}
}

export async function getBoroughs(): Promise<BoroughRecord[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	try {
		// prettier-ignore
		const response = await axios.post<{ data: { administrativeBoroughs: BoroughRecord[] } }>(
            endpoint,
            {
                query: `query GetBoroughs {
                    administrativeBoroughs {
                        name
                        municipalityName
                    }
                }`
            }
        )

		const boroughs: BoroughRecord[] = response.data.data.administrativeBoroughs.map(borough => ({
			name: borough["name"],
			municipalityName: borough["municipalityName"],
		}))

		return boroughs.filter(borough => {
			const validated = BoroughRecordSchema.safeParse(borough)

			if (!validated.success) {
				console.error("Invalid object data:", validated.error.issues)
				return false
			}

			return true
		})
	} catch (error: unknown) {
		throw new Error(`Error fetching boroughs: ${(error as Error).message}`)
	}
}

const transactionQueryData = `
    presentationID
    parcels {
      parcelID
      isActive
    }
    contacts {
      id
      firstName
      lastName
      address
      email
      primaryCompany {
          id
          name
          address
          domain
          isMoral
          neq
          type
          activity
      }
    }
    deedOfSaleDate
    seller {
      id
      name
      adresse
      isMoral
      neq
      type
      activity
    }
    buyer {
      id
      name
      adresse
      isMoral
      neq
      type
      activity
    }
    location {
      coordinates {
        latitude
        longitude
      }
      address
      municipality
      borough
    }
    superficy {
      squareMeters
      squareFeet
    }
    id
    inscriptionNumber
    date
    psaDate
    type
    salePrice
    dataRoomLink
    driveFolderId
    comments
    address
    city
    postalCode
    saleContext
    broker
    agency
    marketplace
    marketplaceId
    mortgage
    salesBalance
    lender
    lenderAmount
    pricePerLandSqft
    pricePerDevelopableSqft
    pricePerBuildableSqft
    pricePerUnit
    landSubtype
    authorizedUses
    currentState
    tod
    codificationType
    projectApproval
    projectApprovalAtDeedOfSale
    projectDocument
    projectLink
    project3dModelLink
    stories
    metadataStoriesStatus
    siteCoverageRatio
    density
    buildableAreaSqft
    metadataBuildableAreaStatus
    numberOfUnits
    metadataNumberOfUnitsStatus
    parkingOutdoorCount
    parkingIndoorCount
    parkingTotal
    parkingRatio
    projectComments
    unitAreaSqft
    cptaq
    urbanPerimeter
    infrastructure
    landShape
    parcelReport
    contamination
    contaminationCost
    wetlands
    floodableZone
    endangeredSpecies
    deductionRatio
    deductionComments
    deductionFiles
    developableLandAreaSqft
    infrastructureDeductionRatio
    infrastructureDeductionComments
    netLandAreaSqft
    hasLegacy
    category
    subcategory
    constructabilityComments
    classificationComments
    buyerRepresentativeLastName
    buyerRepresentativeFirstName
    sellerRepresentativeLastName
    sellerRepresentativeFirstName
    ownershipPercentage
    planMapUrl
    zoningImageUrl
    volumetricImageUrl
    cityInformationSheetUrl
    deedOfSaleUrl
    zoningReportUrl
    landCategory
    isGrossLand
    metadataStatus
    metadataBuyerStatus
    metadataDoneAt
    metadataBuyerDoneAt
    assetType
    builtCategories
    builtSubcategories
    landCategoryNew
    landSubcategory
`

const transactionDetailQueryData = `
    ${transactionQueryData}
    driveFileCount
`

export async function searchTransactions(
	apiToken: string,
	limit: number,
	offset: number,
	filters: Filters,
	sortBy?: string,
	sortOrder?: "asc" | "desc"
): Promise<{ items: Transaction[]; total: number }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const input = {
		municipalities: filters.municipalities,
		administrativeRegions: filters.regions.map(name => ({ name })),
		administrativeBoroughs: filters.boroughs.map(name => ({ name })),
		landAreaRange: [filters.rangeFilters.superficy.min, filters.rangeFilters.superficy.max],
		priceRange: [filters.rangeFilters.price.min, filters.rangeFilters.price.max],
		transactionDateRange: [filters.rangeFilters.date.min, filters.rangeFilters.date.max],
		assetType: filters.assetTypes,
		landCategoryNew: filters.landCategoriesNew,
		landSubcategory: filters.landSubcategories,
		statuses: filters.transactionStatuses,
		source: filters.transactionSource ?? undefined,
		composition: filters.transactionComposition ?? undefined,
		search: filters.transactionSearch || undefined,
	}
	const response = await axios.post<{ data: { searchTransactions: { items: any[]; total: number } } }>(
		endpoint,
		{
			query: `query SearchTransactions($limit: Int, $offset: Int, $input: SearchContactTransactionsInput, $sortBy: String, $sortOrder: String) {
				searchTransactions(limit: $limit, offset: $offset, input: $input, sortBy: $sortBy, sortOrder: $sortOrder) {
					total
					items {
						${transactionQueryData}
					}
				}
			}`,
			variables: { limit, offset, input, sortBy, sortOrder },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	try {
		const { items: raw, total } = response.data.data.searchTransactions
		const items = raw.map(parseTransactionWithComponents).filter((tx): tx is Transaction => tx != null)
		return { items, total }
	} catch (error: any) {
		throw new Error(error)
	}
}

export async function searchTransactionsByView(
	apiToken: string,
	view: View,
	limit: number,
	offset: number
): Promise<{ items: Transaction[]; total: number }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const viewInput = toApiViewInput(view)
	const response = await axios.post<{ data: { searchTransactions: { items: any[]; total: number } } }>(
		endpoint,
		{
			query: `query SearchTransactions($limit: Int, $offset: Int, $view: ViewInput) {
				searchTransactions(limit: $limit, offset: $offset, view: $view) {
					total
					items {
						${transactionQueryData}
					}
				}
			}`,
			variables: { limit, offset, view: viewInput },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	try {
		const { items: raw, total } = response.data.data.searchTransactions
		const items = raw.map(parseTransactionWithComponents).filter((tx): tx is Transaction => tx != null)
		return { items, total }
	} catch (error: any) {
		throw new Error(error)
	}
}

export async function getTransactionsByInscriptionNumber(apiToken: string, num: string): Promise<Transaction[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `query TransactionsByInscriptionNumber($inscriptionNumber: String!) {
                transactionsByInscriptionNumber(inscriptionNumber: $inscriptionNumber) {
                    ${transactionDetailQueryData}
                    components {
                        ${transactionQueryData}
                    }
                }
            }`,
            variables: {
                inscriptionNumber: num,
            },
        },
        {
            headers: {
                "x-api-token": apiToken,
            },
        }
    )

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return []
	}

	// Validation and Data mapping
	const transactions: Transaction[] = response.data.data.transactionsByInscriptionNumber
		.map((tr: Transaction) => parseTransactionWithComponents(tr))
		.filter(Boolean)

	return transactions
}

export async function updateTransaction(
	apiToken: string,
	inscriptionNumber: string,
	overrides: Partial<Transaction>
): Promise<Transaction> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const overridesWithMetaInfo = {
		inscriptionNumber,
		...overrides,
	}

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `mutation UpdateTransaction($overrides: UpdateTransactionInput!) {
                updateTransaction(overrides: $overrides) {
					${transactionDetailQueryData}
                    components {
                        ${transactionQueryData}
                    }
                }
            }`,
            variables: {
                inscriptionNumber,
                overrides: overridesWithMetaInfo,
            },
        },
        {
            headers: {
                'x-api-token': apiToken,
            },
        }
    );

	if (response.data.errors) {
		const messages = response.data.errors.map((err: ApiError) => err.message)
		throw new Error(messages.join("; "))
	}

	const transaction = parseTransactionWithComponents(response.data.data.updateTransaction)
	if (!transaction) {
		throw new Error("Failed to parse updated transaction")
	}

	return transaction
}

export async function createTransaction(
	apiToken: string,
	inscriptionNumber: string,
	parcelIds: string[]
): Promise<Transaction> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `mutation CreateTransaction($transaction: CreateTransactionInput!) {
                createTransaction(transaction: $transaction) {
                    ${transactionQueryData}
                    components {
                        ${transactionQueryData}
                    }
                }
            }`,
            variables: {
                transaction: {
                    inscriptionNumber,
                    parcelIds,
                }
            },
        },
        {
            headers: {
                'x-api-token': apiToken,
            },
        }
    );

	if (response.data.errors) {
		const messages = response.data.errors.map((err: ApiError) => err.message)
		throw new Error(messages.join("; "))
	}

	const transaction = parseTransactionWithComponents(response.data.data.createTransaction)
	if (!transaction) {
		throw new Error("Failed to parse created transaction")
	}

	return transaction
}

export async function createLandAssemblyTransaction(
	apiToken: string,
	componentInscriptionNumbers: string[]
): Promise<Transaction> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `mutation CreateTransaction($transaction: CreateTransactionInput!) {
                createTransaction(transaction: $transaction) {
                    ${transactionQueryData}
                    components {
                        ${transactionQueryData}
                    }
                }
            }`,
            variables: {
                transaction: {
                    parcelIds: [],
                    componentInscriptionNumbers,
                }
            },
        },
        {
            headers: {
                'x-api-token': apiToken,
            },
        }
    );

	if (response.data.errors) {
		const messages = response.data.errors.map((err: ApiError) => err.message)
		throw new Error(messages.join("; "))
	}

	const transaction = parseTransactionWithComponents(response.data.data.createTransaction)
	if (!transaction) {
		throw new Error("Failed to parse created transaction")
	}

	return transaction
}

export async function removeContactFromTransaction(
	apiToken: string,
	inscriptionNumber: string,
	contactId: string
): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `mutation DeleteContactTransaction($contactId: ID!, $inscriptionNumber: String!) {
                deleteContactTransaction(contactId: $contactId, inscriptionNumber: $inscriptionNumber)
                }`,
                variables: {
                    "contactId": contactId,
                    "inscriptionNumber": inscriptionNumber,
                }
        },
        {
            headers: {
                'x-api-token': apiToken,
            },
        }
    )

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return false
	}

	return response.data.data.deleteContactTransaction
}

export async function searchContactTransactions(
	apiToken: string,
	filters: Filters,
	limit: number,
	offset: number
): Promise<{ items: Contact[]; total: number }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const input = Object.freeze({
		municipalities: filters.municipalities,
		administrativeRegions: filters.regions.map(name => ({ name })),
		administrativeBoroughs: filters.boroughs.map(name => ({ name })),
		landAreaRange: [filters.rangeFilters.superficy.min, filters.rangeFilters.superficy.max],
		priceRange: [filters.rangeFilters.price.min, filters.rangeFilters.price.max],
		transactionDateRange: [filters.rangeFilters.date.min, filters.rangeFilters.date.max],
		assetType: filters.assetTypes,
		landCategoryNew: filters.landCategoriesNew,
		landSubcategory: filters.landSubcategories,
		buyerStatus: filters.transactionBuyerStatus ?? undefined,
		hasContactDetails: filters.hasContactDetails,
	})

	const response = await axios.post(
		endpoint,
		{
			query: `query SearchContactTransactions($input: SearchContactTransactionsInput, $limit: Int, $offset: Int) {
				searchContactTransactions(input: $input, limit: $limit, offset: $offset) {
					total
					items {
						hubSpotId
						firstName
						lastName
						email
						companyName
						crmContactId
						representativeId
						crmCompanyId
						inscriptionNumber
						inscriptionNumbers
						assetType
						landCategoryNew
						landSubcategory
						transactions {
							inscriptionNumber
							address
							salePrice
							assetType
							landCategoryNew
							landSubcategory
							pricePerLandSqft
							pricePerBuildableSqft
							pricePerUnit
							buildableAreaSqft
							numberOfUnits
							deedOfSaleDate
						}
					}
				}
			}`,
			variables: { input, limit, offset },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	const result = response.data.data?.searchContactTransactions
	const items = (result?.items ?? []).map((contact: Contact) => ({
		...contact,
		inscriptionNumbers: contact.inscriptionNumbers ?? [],
		transactions: (contact as any).transactions ?? [],
		id: contact.crmContactId ?? contact.representativeId,
	}))

	return {
		items,
		total: result?.total ?? items.length,
	}
}

export async function searchBuyersByView(
	apiToken: string,
	view: View,
	limit: number,
	offset: number
): Promise<{ items: Contact[]; total: number }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const viewInput = toApiViewInput(view)
	const response = await axios.post<{ data: { buyers: { items: Contact[]; total: number } }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `query Buyers($view: ViewInput, $limit: Int, $offset: Int) {
				buyers(view: $view, limit: $limit, offset: $offset) {
					total
					items {
						id
						hubSpotId
						firstName
						lastName
						email
						companyName
						crmContactId
						representativeId
						crmCompanyId
						transactions {
							inscriptionNumber
							address
							salePrice
							assetType
							landCategoryNew
							landSubcategory
							pricePerLandSqft
							pricePerBuildableSqft
							pricePerUnit
							buildableAreaSqft
							numberOfUnits
							deedOfSaleDate
						}
					}
				}
			}`,
			variables: { view: viewInput, limit, offset },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) {
		throw new Error(response.data.errors.map(error => error.message).join("; "))
	}

	const result = response.data.data?.buyers
	const items = (result?.items ?? []).map(buyer => {
		const transactions = (buyer.transactions ?? []) as { inscriptionNumber?: string }[]

		return {
			...buyer,
			id: buyer.crmContactId ?? buyer.representativeId,
			inscriptionNumbers: transactions
				.map(transaction => transaction.inscriptionNumber)
				.filter((inscriptionNumber): inscriptionNumber is string => inscriptionNumber != null),
			transactions,
		}
	})

	return {
		items,
		total: result?.total ?? items.length,
	}
}

export async function createBuyersList(apiToken: string, name: string, contactIds: string[]): Promise<BuyersList> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `mutation CreateBuyersList($name: String!, $contactIds: [String]!) {
                createBuyersList(name: $name, contactIds: $contactIds) {
                    id
                    name
                }
            }`,
			variables: { name, contactIds },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		const messages = response.data.errors.map((err: ApiError) => err.message)
		throw new Error(messages.join("; "))
	}
	return response?.data?.data?.createBuyersList
}

// graphql query to get a parcel by its ID
export async function getParcel(parcelID: number): Promise<Parcel | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const variables = { parcelID }
	const query = `
	query ($parcelID: String!) {
		parcel(parcelID: $parcelID) {
		  parcelID,
		  location {
			coordinates {
			  latitude,
			  longitude
			}
		  }
		}
	  }
      `

	try {
		const result = await axios.post(endpoint, {
			query,
			variables,
		})

		const errors = result.data.errors

		if (result.status === 200) {
			if (!errors) {
				let parcelValidation = ParcelSchema.safeParse(result.data.data.parcel)

				if (parcelValidation.success) {
					const parcel = parcelValidation.data
					parcel.location.coordinates.latitude = String(parseFloat(parcel.location.coordinates.latitude))
					parcel.location.coordinates.longitude = String(parseFloat(parcel.location.coordinates.longitude))

					return parcel
				} else {
					console.error("Invalid object data:", result)
					return null
				}
			} else {
				console.error(`GraphQL error: ${errors[0].message}`)
				return null
			}
		} else {
			console.error("Error: Parcel not found", result.status)
			return null
		}
	} catch (error: any) {
		console.error("Error.", error)
		Sentry.captureException(error)
		return null
	}
}

// graphql query to get a prospect by parcel id
export async function getProspect(apiToken: string, parcelID: string | number): Promise<Prospect | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const variables = { parcelID }
	const query = `
        query Prospect($parcelID: String!) {
            prospect(parcelID: $parcelID) {
                prospectID
                potential
            }
        }
    `

	try {
		const result = await axios.post(
			endpoint,
			{
				query,
				variables,
			},
			{
				headers: {
					"x-api-token": apiToken,
				},
			}
		)

		const errors = result.data.errors

		if (result.status === 200) {
			if (errors) {
				console.error(`GraphQL error: ${errors[0].message}`)
				return null
			}

			const prospect = result.data.data.prospect
			if (!prospect) {
				return null
			}

			const validatedProspect = ProspectSchema.safeParse(prospect)

			if (validatedProspect.success) {
				return validatedProspect.data
			} else {
				console.error("Invalid object data:", result)
				return null
			}
		} else {
			console.error("Error: Prospect not found", result.status)
			return null
		}
	} catch (error: any) {
		console.error("Error.", error)
		Sentry.captureMessage(`Network error getProspect id:${parcelID}: ${error}`, "error")
		return null
	}
}

// graphql mutation to update a prospect potential by parcel id
export async function setProspectPotential(
	apiToken: string,
	parcelID: number,
	potential: DevelopmentPotential
): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const query = `
        mutation {
            updateProspectPotential(parcelID: "${parcelID}", potential: ${potential})
        }
    `

	try {
		const result = await axios.post(
			endpoint,
			{
				query,
			},
			{
				headers: {
					"x-api-token": apiToken,
				},
			}
		)

		if (result.status === 200) {
			const errors = result.data.errors

			if (errors) {
				console.error(`GraphQL error: ${errors[0].message}`)
				return false
			}
			return true
		} else {
			console.error("Error: could not update prospect potential", result.status)
			return false
		}
	} catch (error: any) {
		console.error("Error.", error)
		Sentry.captureMessage(`Network error setProspectPotential id:${parcelID}: ${error}`, "error")
		return false
	}
}

// graphql query to get a prospect by parcel id
export async function getUrbanPriority(apiToken: string, parcelId: number): Promise<UrbanPriority[] | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const variables = { parcelId }
	const query = `
		query UrbanPriority($parcelId: String!) {
			urbanPriority(parcelId: $parcelId) {
				planType
				priority
			}
		}
    `

	try {
		const result = await axios.post(endpoint, { query, variables }, { headers: { "x-api-token": apiToken } })
		const errors = result.data.errors

		if (result.status === 200) {
			return result.data.data.urbanPriority
		} else {
			console.error("Error: Prospect not found", result.status, errors)
			return null
		}
	} catch (error: any) {
		console.error("Error.", error)
		return null
	}
}

// Validate ID token and get our own JWT token with all our desired claims
export async function validateIDToken(idToken: string) {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const query = `mutation ValidateIDToken($input: ValidateIDTokenInput!) {
		validateIDToken(input: $input) {
			success
			message
			token
			user {
				id
				email
				firstName
				lastName
				createdAt
			}
			error
		}
	}`

	try {
		const result = await axios.post(endpoint, {
			query,
			variables: {
				input: {
					idToken: idToken,
				},
			},
		})

		if (result.data.errors) {
			throw new Error(result.data.errors[0].message)
		}

		return result.data.data.validateIDToken
	} catch (error: any) {
		throw new Error(error)
	}
}

export type ParcelExportFormat = "shapefile" | "kml"

export async function getShapefile(apiToken: string, parcelID: string): Promise<[Blob, string]> {
	const endpoint = import.meta.env.VITE_GEOM_API_ENDPOINT
	const filename = `parcel-${parcelID}-shapefile.zip`
	const url = urljoin(endpoint, filename)
	try {
		const response = await axios.get(url.toString(), {
			headers: {
				"x-api-token": apiToken,
			},
			responseType: "blob",
		})

		return [response.data, filename]
	} catch (error: any) {
		throw new Error(error)
	}
}

export async function getParcelKml(apiToken: string, parcelID: string): Promise<[Blob, string]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `query ParcelKml($parcelID: String!) {
				parcel(parcelID: $parcelID) {
					kml {
						filename
						contentType
						content
					}
				}
			}`,
			variables: { parcelID },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	const parcelExport = response.data.data?.parcel?.kml
	if (!parcelExport) {
		throw new Error(`KML export unavailable for parcel ${parcelID}`)
	}

	return [new Blob([parcelExport.content], { type: parcelExport.contentType }), parcelExport.filename]
}

export async function getParcelZoning(apiToken: string, parcelID: string): Promise<Zone[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `query ParcelZoning($parcelID: String!) {
				parcel(parcelID: $parcelID) {
					zoning {
						zoneCode
						city
						borough
						gridUrl
						documentUrl
						rules {
							bylawNumber
							printDate
							useGroup
							useClass
							useClassDescription
							permittedUseCodes
							excludedUseCodes
							structureType
							isPrincipalUse
							minFrontSetbackMeters
							maxFrontSetbackMeters
							minSideStreetSetbackMeters
							maxSideStreetSetbackMeters
							sideSetbackMeters
							totalSideSetbackMeters
							rearSetbackMeters
							minHeightFloors
							maxHeightFloors
							minHeightMeters
							maxHeightMeters
							minBuildingWidthMeters
							maxBuildingWidthMeters
							minFootprintSquareMeters
							maxFootprintSquareMeters
							minFloorAreaSquareMeters
							maxFloorAreaSquareMeters
							minUnitsPerBuilding
							maxUnitsPerBuilding
							minNetDensity
							maxNetDensity
							minCoverageRatio
							maxCoverageRatio
							minFloorAreaRatio
							maxFloorAreaRatio
							minLotWidthMeters
							minLotDepthMeters
							minLotAreaSquareMeters
							specialProvisionCodes
							otherProvisionCodes
						}
					}
				}
			}`,
			variables: { parcelID },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data?.parcel?.zoning ?? []
}

export async function getDissolvedShapefile(apiToken: string, parcelIds: string[]): Promise<[Blob, string]> {
	const endpoint = import.meta.env.VITE_GEOM_API_ENDPOINT
	const filename = "selected-parcels-shapefile.zip"
	const url = urljoin(endpoint, filename)
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"x-api-token": apiToken,
		},
		body: JSON.stringify({ parcelIds }),
	})

	if (!response.ok) {
		const responseBody = await response.text()
		throw new Error(
			`Dissolved Shapefile export failed with status ${response.status} from ${response.url}${responseBody ? `: ${responseBody}` : ""}`
		)
	}

	return [await response.blob(), filename]
}

export async function getDissolvedParcelKml(apiToken: string, parcelIDs: string[]): Promise<[Blob, string]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `query ParcelSelectionKml($parcelIDs: [String!]!) {
				parcelSelection(parcelIDs: $parcelIDs) {
					kml {
						filename
						contentType
						content
					}
				}
			}`,
			variables: { parcelIDs },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	const parcelExport = response.data.data?.parcelSelection?.kml
	if (!parcelExport) {
		throw new Error("KML export unavailable for selected parcels")
	}

	return [new Blob([parcelExport.content], { type: parcelExport.contentType }), parcelExport.filename]
}

export async function searchContacts(apiToken: string, name: string): Promise<ContactList | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `query SearchContacts($contactName: String!) {
                searchContacts(contactName: $contactName) {
                    id
                    firstName
                    lastName
                    address
                    email
                    primaryCompany {
                        id
                        name
                        address
                        isMoral
                        neq
                        type
                        activity
                    }
                }
            }`,
            variables: {
                contactName: name,
            },
        },
        {
            headers: {
                "x-api-token": apiToken,
            },
        }
    );

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return null
	}

	// Validation and Data mapping
	const contactData = response.data.data.searchContacts
	if (!contactData) {
		return null
	}

	const result = ContactListSchema.safeParse(contactData)

	if (!result.success) {
		console.error("Invalid object data:", result.error.issues)
		return null
	}

	return result.data
}

// Example API request:
//
//  query SearchCompanies($partialName: String!) {
//    searchCompanies(partialName: $partialName) {
//      id
//      name
//      domain
//      address
//    }
//  }
//
// Example API response:
//
//  {
//    "data": {
//      "searchCompanies": [
//        {
//          "id": "6260495902",
//          "name": "T.G. Beco ltee",
//          "domain": "tgbeco.com",
//          "address": "5967 Rue Jean-Talon"
//        },
//        {
//          "id": "7741883995",
//          "name": "Construction Quali-T",
//          "domain": "constructionquali-t.com",
//          "address": null
//        }
//      ]
//    }
//  }
export async function searchCompanies(apiToken: string, partialName: string): Promise<CompanyList> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `query SearchCompanies($partialName: String!) {
                searchCompanies(partialName: $partialName) {
                    id
                    name
                    domain
                    address
                }
            }`,
			variables: {
				partialName,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return []
	}

	// Validation and Data mapping
	const data = response.data.data.searchCompanies
	if (!data) {
		return []
	}

	const result = CompanyListSchema.safeParse(data)

	if (!result.success) {
		console.error("Invalid object data:", result.error.issues)
		return []
	}

	return result.data
}

export async function addContactToTransaction(
	apiToken: string,
	contactId: string,
	role: string,
	inscriptionNumber: string,
	primaryCompanyId?: string
): Promise<Contact | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	// prettier-ignore
	const response = await axios.post(
        endpoint,
        {
            query: `mutation AddContactTransaction($inscriptionNumber: String!, $contactId: ID!, $role: Role!, $primaryCompanyId: ID) {
              addContactTransaction(inscriptionNumber: $inscriptionNumber, contactId: $contactId, role: $role, primaryCompanyId: $primaryCompanyId) {
                id
                adresse
                address
                firstName
                lastName
                email
                hubspotId
                primaryCompany {
                  id
                  name
                  adresse
                  address
                  isMoral
                  neq
                  type
                  activity
                }
              }
            }`,
            variables: {
                inscriptionNumber,
                contactId: contactId,
                role,
                primaryCompanyId: primaryCompanyId || null,
                source: "app",
            },
        },
        {
            headers: {
                "x-api-token": apiToken,
            },
        }
    );

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return null
	}

	return response.data.data.addContactTransaction
}

export async function createOpportunity(
	apiToken: string,
	input: {
		price?: number
		developmentType?: string[]
		dataRoomCloudFile?: string
		informationOnExistingConditions?: string
		informationOnPotentialAndZoning?: string
		buildableAreaSqft?: number
		stage?: string
		totalAreaSqft?: number
		ownerInformation?: string
		parcelIDs?: string[]
		projectStatus?: string[]
		city?: string
		expirationDate?: string | null
		launchDate?: string | null
		pipeline?: string
		postalCode?: string
		region?: string
		province?: string
		streetAddress?: string
		webpageLink?: string
		marketingType?: string
		projectId?: string
		ccaContractHubspotId?: string | null
		parentId?: string
	}
): Promise<number | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `mutation CreateOpportunity($input: OpportunityInput!) {
				createOpportunity(input: $input)
			}`,
			variables: {
				input,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return null
	}

	return response.data.data.createOpportunity
}

export interface CreateTestOpportunitiesInput {
	projectId: string | null
	count: number
	parcelIds: string[]
	opportunityType: OpportunityType
	priority: OpportunityPriority | null
	assetTypes: string[]
	landCategories: string[]
	landSubcategories: string[]
	transactionStatuses: string[]
	transactionVerificationStatus: TransactionVerificationStatus | null
	transactionBuyerStatus: string | null
	transactionSource: string | null
	transactionComposition: string | null
	sizeSqft: { min: number; max: number }
	buildableSqft: { min: number; max: number }
	price: { min: number; max: number }
	residentialUnits: { min: number; max: number }
	regions: string[]
	municipalities: string[]
	boroughs: string[]
}

interface CreateTestOpportunitiesResult {
	createdCount: number
	parcelIds: string[]
	opportunityIds: string[]
}

export async function createTestOpportunities(
	apiToken: string,
	input: CreateTestOpportunitiesInput
): Promise<CreateTestOpportunitiesResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `mutation CreateTestOpportunities($input: CreateTestOpportunitiesInput!) {
				createTestOpportunities(input: $input) {
					createdCount
					parcelIds
					opportunityIds
				}
			}`,
			variables: {
				input,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to create opportunities")
	}

	return response.data.data.createTestOpportunities
}

export async function inviteContactsToProject(
	apiToken: string,
	projectId: string,
	invitees: Array<{ email: string; firstName?: string; lastName?: string }>
): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `mutation InviteContactsToProject($projectId: UUID!, $invitees: [ProjectInviteeInput!]!) {
				inviteContactsToProject(projectId: $projectId, invitees: $invitees)
			}`,
			variables: {
				projectId,
				invitees,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return false
	}

	return response.data.data.inviteContactsToProject === true
}

export async function getCCAContracts(apiToken: string): Promise<{ hubspotRecordId: string; contractName: string }[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `query GetCCAContracts {
				ccaContracts {
					hubspotRecordId
    				contractName
				}
			}`,
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return []
	}

	return response.data.data.ccaContracts
}

export async function getCCAClients(
	apiToken: string,
	ccaContractHubspotId: string
): Promise<{ id: string; firstName: string | null; lastName: string | null; email: string | null }[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `query GetCCAClients($ccaContractHubspotId: String!) {
				ccaClients(ccaContractHubspotId: $ccaContractHubspotId) {
					id
					firstName
					lastName
					email
				}
			}`,
			variables: {
				ccaContractHubspotId,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return []
	}

	return response.data.data.ccaClients
}
export async function getUsersByRole(
	apiToken: string,
	role: string
): Promise<{ name: string; id: string; email: string }[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `query($role: String) {
						users(role: $role) {
							id
							name
						}
				}
			`,
			variables: {
				role: role,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => {
			console.error(message)
		})
		return []
	}

	return response.data.data.users
}

export async function getUsersByPermission(
	apiToken: string,
	permission: string
): Promise<{ name: string; id: string }[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `query($permission: String) {
						users(permission: $permission) {
							id
							name
						}
				}
			`,
			variables: { permission },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) {
		response.data.errors.forEach(({ message }: { message: string }) => console.error(message))
		return []
	}

	return response.data.data.users
}

export async function getUsers(apiToken: string): Promise<any[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
			query GetUsers {
				users {
					id
					email
					name
					isActive
					createdAt
					updatedBy {
						name
					}
					roles {
						id
						name
						permissions {
							id
							name
							description
						}
					}
				}
			}
		`,
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.users
}

export async function getRoles(apiToken: string): Promise<any[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
			query GetRoles {
				roles {
					id
					name
				}
			}
		`,
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.roles
}

export async function assignUserRoles(apiToken: string, userId: number, roleIds: number[]): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
			mutation assignUserRoles($userId: Int!, $roleIds: [Int!]!) {
				assignUserRoles(userId: $userId, roleIds: $roleIds)
			}
		`,
			variables: {
				userId,
				roleIds,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.assignUserRoles
}

type CreateUserPayload = {
	firstName: string
	lastName: string
	email: string
	roleId: number
}

export async function createUser(apiToken: string, input: CreateUserPayload): Promise<any> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
			mutation CreateUser($input: CreateUserInput!) {
				createUser(input: $input) {
					id
					email
					name
					firstName
					lastName
					isActive
					createdAt
					updatedBy {
						name
					}
					roles {
						id
						name
						permissions {
							id
							name
							description
						}
					}
				}
			}
		`,
			variables: {
				input,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.createUser
}

export async function revokeUserToken(apiToken: string, userId: number): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation revokeUserToken($userId: Int!) {
					revokeUserToken(userId: $userId)
				}
			`,
			variables: {
				userId,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.revokeUserToken
}

export type ServiceToken = {
	id: string
	name: string
	permissions: string[]
	createdBy: string
	createdByUser: { id: string; email: string; name: string | null }
	expiresAt: string
	revokedAt: string | null
	revokedByUser: { id: string; email: string; name: string | null } | null
	lastUsedAt: string | null
}

type CreatedServiceToken = {
	serviceToken: ServiceToken
	token: string
}

export async function getServiceTokens(apiToken: string): Promise<ServiceToken[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
				query ServiceTokens {
					serviceTokens {
						id
						name
						permissions
						createdBy
						createdByUser { id email name }
						expiresAt
						revokedAt
						revokedByUser { id email name }
						lastUsedAt
					}
				}
			`,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) throw new Error(response.data.errors[0].message)
	return response.data.data.serviceTokens
}

export async function createServiceToken(
	apiToken: string,
	input: { name: string; permissions: string[]; expiresIn?: string }
): Promise<CreatedServiceToken> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation CreateServiceToken($name: String!, $permissions: [String!]!, $expiresIn: String) {
					createServiceToken(name: $name, permissions: $permissions, expiresIn: $expiresIn) {
						serviceToken {
							id
							name
							permissions
							createdBy
							createdByUser { id email name }
							expiresAt
							revokedAt
							revokedByUser { id email name }
							lastUsedAt
						}
						token
					}
				}
			`,
			variables: input,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) throw new Error(response.data.errors[0].message)
	return response.data.data.createServiceToken
}

export async function revokeServiceToken(apiToken: string, id: string): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation RevokeServiceToken($id: ID!) {
					revokeServiceToken(id: $id)
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) throw new Error(response.data.errors[0].message)
	return response.data.data.revokeServiceToken
}

export async function archiveUser(apiToken: string, userId: number): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation archiveUser($userId: Int!) {
					archiveUser(userId: $userId)
				}
			`,
			variables: {
				userId,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.archiveUser
}

export async function loginAsUser(
	apiToken: string,
	userId: number
): Promise<{ success: boolean; token: string | null; user: any; error: string | null; message: string | null }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
			mutation LoginAsUser($userId: Int!) {
				loginAsUser(userId: $userId) {
					success
					token
					user {
						id
						email
						firstName
						lastName
					}
					error
					message
				}
			}
		`,
			variables: {
				userId,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.loginAsUser
}

type RolePermission = {
	id: string
	name: string
	description: string | null
}

export type RoleWithPermissions = {
	id: string
	name: string
	description: string | null
	permissions: RolePermission[]
}

export async function getRolesWithPermissions(apiToken: string): Promise<RoleWithPermissions[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
		query GetRolesWithPermissions {
			roles {
				id
				name
				description
				permissions {
					id
					name
					description
				}
			}
		}
		`,
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.roles
}

export async function getAllPermissions(apiToken: string): Promise<RolePermission[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
		query GetAllPermissions {
			permissions {
				id
				name
				description
			}
		}
		`,
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.permissions
}

export async function createPermission(
	apiToken: string,
	name: string,
	description: string
): Promise<{ id: string; name: string; description: string | null }> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation CreatePermission($name: String!, $description: String!) {
					createPermission(name: $name, description: $description) {
						id
						name
						description
					}
				}
			`,
			variables: {
				name,
				description: description.trim(),
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.createPermission
}

export async function assignPermissionToRole(apiToken: string, roleId: number, permissionIds: number[]): Promise<any> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const response = await axios.post(
		endpoint,
		{
			query: `
			mutation assignPermissionToRole($roleId: Int!, $permissionIds: [Int!]!) {
				assignPermissionToRole(roleId: $roleId, permissionIds: $permissionIds) {
					id
					name
					description
					permissions {
						id
						name
						description
					}
				}
			}
		`,
			variables: {
				roleId,
				permissionIds,
			},
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0].message)
	}

	return response.data.data.assignPermissionToRole
}

export type ProjectTypeFilter = "CCA" | "CCV" | "WORKSPACE"

export interface CurrentUserProjectPerson {
	id: string
	name?: string | null
}

export interface CurrentUserProject {
	id: string
	name: string
	createdAt: string
	updatedAt: string
	expiryDate?: string | null
	type?: ProjectTypeFilter
	ccaContractHubspotId: string
	brokers: CurrentUserProjectPerson[]
	prospectors: CurrentUserProjectPerson[]
	targetsCount: number
}

export interface CurrentUserDetails {
	phoneNumber?: string | null
	phoneVerifiedAt?: string | null
	preferredLanguage?: string | null
	unit?: Unit | null
	projects: CurrentUserProject[]
}

export interface CurrentUserBasicDetails {
	firstName?: string | null
	lastName?: string | null
	phoneNumber?: string | null
	phoneVerifiedAt?: string | null
	preferredLanguage?: string | null
	defaultPage?: Page | null
	unit?: Unit | null
}

type CurrentUserResponse = CurrentUserDetails & Pick<CurrentUserBasicDetails, "firstName" | "lastName" | "defaultPage">

type FetchCurrentUserOptions = {
	includeProjects?: boolean
	includeProjectPeople?: boolean
}

type ProjectFetchOptions = {
	includePeople?: boolean
}

interface PreferredLanguageResult {
	preferredLanguage?: string | null
}

interface DefaultPageResult {
	defaultPage?: Page | null
}

interface UnitResult {
	unit?: Unit | null
}

interface PersonalInfoResult {
	firstName?: string | null
	lastName?: string | null
}

function throwCurrentUserError(response: { data: { errors?: ApiError[] } }): void {
	if (!response.data.errors) return

	// Check if token is revoked
	// Error could be both TOKEN_REVOKED or "User ID not found"
	const tokenRevokedError = response.data.errors.find((error: any) => error.extensions?.code === "TOKEN_REVOKED")

	const userNotFoundError = response.data.errors.find((error: any) => error.message === "User ID not found")

	if (tokenRevokedError || userNotFoundError) {
		import("@/utils/tokenRevocation").then(({ handleTokenRevocation }) => {
			handleTokenRevocation()
		})
		throw new Error("Token has been revoked. Please log in again.")
	}

	throw new Error(response.data.errors[0].message)
}

export async function fetchCurrentUser(
	apiToken: string,
	options: FetchCurrentUserOptions = {}
): Promise<CurrentUserResponse> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const includeProjects = options.includeProjects ?? true
	const includeProjectPeople = options.includeProjectPeople ?? true
	const projectPeopleFields = includeProjectPeople
		? `
							brokers {
								id
								name
							}
							prospectors {
								id
								name
							}
		`
		: ""
	const projectsFields = includeProjects
		? `
							projects {
								id
								name
								createdAt
								updatedAt
								expiryDate
								type
								ccaContractHubspotId
								${projectPeopleFields}
								targetsCount
							}
		`
		: ""

	const response = await axios.post(
		endpoint,
		{
			query: `
				query GetCurrentUser {
					currentUser {
						firstName
						lastName
						phoneNumber
						phoneVerifiedAt
						preferredLanguage
						defaultPage
						unit
						${projectsFields}
					}
				}
			`,
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwCurrentUserError(response)

	const currentUser = response.data.data.currentUser
	return {
		...currentUser,
		projects: (currentUser.projects ?? []).map((project: CurrentUserProject) => ({
			...project,
			brokers: project.brokers ?? [],
			prospectors: project.prospectors ?? [],
		})),
	}
}

export async function setPreferredLanguage(
	apiToken: string,
	preferredLanguage: string
): Promise<PreferredLanguageResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { setPreferredLanguage: PreferredLanguageResult }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation SetPreferredLanguage($preferredLanguage: String!) {
					setPreferredLanguage(preferredLanguage: $preferredLanguage) {
						preferredLanguage
					}
				}
			`,
			variables: { preferredLanguage },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwGraphQLError(response, "Failed to update preferred language")

	return response.data.data.setPreferredLanguage
}

export async function setDefaultPage(apiToken: string, defaultPage: Page): Promise<DefaultPageResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { setDefaultPage: DefaultPageResult }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation SetDefaultPage($defaultPage: Page!) {
					setDefaultPage(defaultPage: $defaultPage) {
						defaultPage
					}
				}
			`,
			variables: { defaultPage },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwGraphQLError(response, "Failed to update default page")

	return response.data.data.setDefaultPage
}

export async function setUnit(apiToken: string, unit: Unit): Promise<UnitResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { setUnit: UnitResult }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation SetUnit($unit: Unit!) {
					setUnit(unit: $unit) {
						unit
					}
				}
			`,
			variables: { unit },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwGraphQLError(response, "Failed to update unit")

	return response.data.data.setUnit
}

export async function updateCurrentUserPersonalInfo(
	apiToken: string,
	firstName: string,
	lastName: string
): Promise<PersonalInfoResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { updateCurrentUserPersonalInfo: PersonalInfoResult }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation UpdateCurrentUserPersonalInfo($firstName: String!, $lastName: String!) {
					updateCurrentUserPersonalInfo(firstName: $firstName, lastName: $lastName) {
						firstName
						lastName
					}
				}
			`,
			variables: { firstName, lastName },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	throwGraphQLError(response, "Failed to update personal information")

	return response.data.data.updateCurrentUserPersonalInfo
}

export interface ProjectListItem {
	id: string
	name: string
	type: ProjectTypeFilter
	createdAt: string
}

type CustomViewConfig = Record<string, unknown>

export interface CustomView {
	id: string
	name: string
	filterConfig: CustomViewConfig
	sortConfig: CustomViewConfig
	displayConfig: CustomViewConfig
	opportunityIds: string[]
	createdAt: string
	updatedAt: string
}

interface CreateCustomViewInput {
	name: string
	projectId?: string | null
	filterConfig?: CustomViewConfig
	sortConfig?: CustomViewConfig
	displayConfig?: CustomViewConfig
	opportunityIds?: Array<string | number>
}

type UpdateCustomViewInput = Partial<Omit<CreateCustomViewInput, "projectId">>

export interface NotificationPreference {
	id: string
	channel: string
	enabled: boolean
	frequency: string
	consentedAt?: string | null
	optedOutAt?: string | null
	createdAt: string
	updatedAt: string
}

export interface Notification {
	id: string
	topic: NotificationTopic
	title: string
	payload: Record<string, unknown>
	readAt?: string | null
	createdAt: string
	updatedAt: string
}

interface PaginatedNotifications {
	items: Notification[]
	total: number
}

interface SetNotificationPreferenceInput {
	channel: string
	enabled: boolean
	frequency?: string | null
}

export interface CreateNotificationInput {
	userProfileId: number
	topic?: string | null
	title: string
	message?: string | null
	projectId?: string | null
	customViewId?: string | null
	opportunityIds?: string[]
	actionLabel?: string | null
}

export type CreateEmailInput = CreateNotificationInput

export type EmailTemplatePreviewFieldInputType =
	| "text"
	| "textarea"
	| "email"
	| "url"
	| "number"
	| "boolean"
	| "json"
	| "radio"

export interface EmailTemplatePreviewField {
	name: string
	label: string
	inputType: EmailTemplatePreviewFieldInputType
	required: boolean
	defaultValue?: unknown
	helpText?: string | null
	options?: { label: string; value: string }[] | null
}

export interface EmailTemplatePreviewTemplate {
	id: string
	name: string
	description: string
	fields: EmailTemplatePreviewField[]
}

interface PreviewEmailTemplateInput {
	template: string
	values?: Record<string, unknown> | null
}

export interface RenderedEmailTemplatePreview {
	template: string
	subject: string
	previewText?: string | null
	html: string
	text?: string | null
}

export interface SendSmsInput {
	userProfileId: number
	message: string
}

interface PhoneVerificationStartResult {
	phoneNumber: string
}

interface PhoneVerificationResult {
	approved: boolean
	phoneNumber: string
	phoneVerifiedAt?: string | null
}

const customViewFields = `
	id
	name
	filterConfig
	sortConfig
	displayConfig
	opportunityIds
	createdAt
	updatedAt
`

function searchContextLayerFields(depth = 4): string {
	return `
		name
		active
		${depth > 0 ? `sublayers { ${searchContextLayerFields(depth - 1)} }` : ""}
	`
}

const viewFields = `
	scope
	select {
		parcel
		opportunity
		transaction
	}
	filter {
		parcel {
			superficy {
				min
				max
			}
			dwellingUnits {
				min
				max
			}
			storeys {
				min
				max
			}
			propertyValue {
				min
				max
			}
			buildingAge {
				min
				max
			}
			ppuArea
			todArea
			soilContaminated
			wetland
			rainette
			showStreets
		}
		opportunity {
			opportunityTypes
			developmentTypes
			regions
			municipalities
			boroughs
			price {
				min
				max
			}
			landArea {
				min
				max
			}
			buildableLandArea {
				min
				max
			}
			residentialUnits {
				min
				max
			}
			launchDate {
				min
				max
			}
			assetTypes
			landCategories
			landSubcategories
			evaluationStatus
			priority
			brokers
		}
		transaction {
			price {
				min
				max
			}
			landArea {
				min
				max
			}
			date {
				min
				max
			}
			regions
			municipalities
			boroughs
			statuses
			verificationStatus
			source
			composition
			buyerStatus
			assetTypes
			builtCategories
			builtSubcategories
			landCategories
			landSubcategories
		}
	}
	order {
		field
		direction
	}
`

const searchContextFields = `
	id
	name
	layers {
		${searchContextLayerFields()}
	}
	views {
		${viewFields}
	}
	display {
		mapStyle
	}
	createdAt
	updatedAt
`

const notificationPreferenceFields = `
	id
	channel
	enabled
	frequency
	consentedAt
	optedOutAt
	createdAt
	updatedAt
`

const notificationFields = `
	id
	topic
	title
	payload
	readAt
	createdAt
	updatedAt
`

const projectOpportunityTargetFields = `
	id
	updatedAt
	comment
	hubspotDealId
	hubspotParentDealId
`

const projectBaseFields = `
	id
	name
	type
	createdAt
	updatedAt
	ccaContractHubspotId
	expiryDate
	viewConfig
`

const projectPeopleFields = `
	brokers {
		id
		firstName
		lastName
		email
	}
	prospectors {
		id
		firstName
		lastName
		email
	}
	clients {
		id
		firstName
		lastName
		email
	}
`

function projectFields(options: ProjectFetchOptions = {}): string {
	const includePeople = options.includePeople ?? true
	return `
		${projectBaseFields}
		${includePeople ? projectPeopleFields : ""}
		searchContext {
			${searchContextFields}
		}
	`
}

function throwGraphQLError(response: { data: { errors?: ApiError[] } }, fallbackMessage: string): void {
	if (response.data.errors) {
		const tokenRevokedError = response.data.errors.find(error => error.extensions?.code === "TOKEN_REVOKED")
		if (tokenRevokedError) {
			import("@/utils/tokenRevocation").then(({ handleTokenRevocation }) => {
				handleTokenRevocation()
			})
			throw new Error("Token has been revoked. Please log in again.")
		}

		throw new Error(response.data.errors[0]?.message ?? fallbackMessage)
	}
}

export async function getProjects(apiToken: string, type?: ProjectTypeFilter | null): Promise<ProjectListItem[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `
				query GetProjects($type: ProjectType) {
					projects(type: $type) {
						id
						name
						type
						createdAt
					}
				}
			`,
			variables: { type: type ?? null },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to fetch projects")
	}

	return response.data.data.projects ?? []
}

export async function fetchProjectMetadata(
	apiToken: string,
	projectId: string,
	options: ProjectFetchOptions = {}
): Promise<Project | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { project: Project | null }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				query Project($projectId: UUID!) {
					project(id: $projectId) {
						${projectFields(options)}
						opportunityTargets {
							${projectOpportunityTargetFields}
						}
					}
				}
			`,
			variables: { projectId },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch project")

	return response.data.data.project ?? null
}

export async function createProject(apiToken: string, project: ProjectInput): Promise<Project | null> {
	return upsertProject(apiToken, project)
}

export async function updateProject(apiToken: string, project: ProjectInput): Promise<Project | null> {
	return upsertProject(apiToken, project)
}

export async function createWorkspace(apiToken: string, userProfileId: number): Promise<Project | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { createWorkspace: Project | null }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation CreateWorkspace($userProfileId: ID!) {
					createWorkspace(userProfileId: $userProfileId) {
						${projectFields({ includePeople: false })}
					}
				}
			`,
			variables: { userProfileId: String(userProfileId) },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to create workspace")

	return response.data.data.createWorkspace ?? null
}

export async function getUserHasWorkspace(apiToken: string, userProfileId: number): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { userHasWorkspace: boolean }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				query UserHasWorkspace($userProfileId: ID!) {
					userHasWorkspace(userProfileId: $userProfileId)
				}
			`,
			variables: { userProfileId: String(userProfileId) },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to check workspace")

	return response.data.data.userHasWorkspace
}

export async function addOpportunityTargetComment(
	apiToken: string,
	opportunityTargetId: number,
	comment: string
): Promise<OpportunityTarget> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { addOpportunityTargetComment: OpportunityTarget | null }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation AddOpportunityTargetComment($input: AddOpportunityTargetCommentInput!) {
					addOpportunityTargetComment(input: $input) {
						${projectOpportunityTargetFields}
					}
				}
			`,
			variables: { input: { opportunityTargetId, comment } },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to add opportunity target comment")

	const opportunityTarget = response.data.data.addOpportunityTargetComment
	if (opportunityTarget == null) {
		throw new Error("Failed to add opportunity target comment")
	}

	return opportunityTarget
}

async function upsertProject(apiToken: string, project: ProjectInput): Promise<Project | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { upsertProject: Project | null }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation upsertProject($input: ProjectInput!) {
					upsertProject(input: $input) {
						${projectFields({ includePeople: true })}
						opportunityTargets {
							${projectOpportunityTargetFields}
						}
					}
				}
			`,
			variables: { input: project },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to save project")

	return response.data.data.upsertProject
}

export async function fetchSearchContexts(
	apiToken: string,
	options: { userProfileId?: string | number | null; projectId?: string | null } = {}
): Promise<SearchContext[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { searchContexts: SearchContext[] }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				query SearchContexts($userProfileId: ID, $projectId: UUID) {
					searchContexts(userProfileId: $userProfileId, projectId: $projectId) {
						${searchContextFields}
					}
				}
			`,
			variables: {
				userProfileId: options.userProfileId,
				projectId: options.projectId ?? null,
			},
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch search contexts")

	return response.data.data.searchContexts ?? []
}

export async function fetchSearchContext(apiToken: string, id: string): Promise<SearchContext | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { searchContext: SearchContext | null }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				query SearchContext($id: ID!) {
					searchContext(id: $id) {
						${searchContextFields}
					}
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch search context")

	return response.data.data.searchContext ?? null
}

export async function createSearchContext(apiToken: string, input: CreateSearchContextInput): Promise<SearchContext> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { createSearchContext: SearchContext }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation CreateSearchContext($input: CreateSearchContextInput!) {
					createSearchContext(input: $input) {
						${searchContextFields}
					}
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to create search context")

	return response.data.data.createSearchContext
}

export async function updateSearchContext(
	apiToken: string,
	id: string,
	input: UpdateSearchContextInput
): Promise<SearchContext> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { updateSearchContext: SearchContext }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation UpdateSearchContext($id: ID!, $input: UpdateSearchContextInput!) {
					updateSearchContext(id: $id, input: $input) {
						${searchContextFields}
					}
				}
			`,
			variables: { id, input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to update search context")

	return response.data.data.updateSearchContext
}

export async function updateProjectSearchContext(
	apiToken: string,
	projectId: string,
	input: ProjectSearchContextInput
): Promise<SearchContext> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { updateProjectSearchContext: SearchContext }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation UpdateProjectSearchContext($projectId: UUID!, $input: ProjectSearchContextInput!) {
					updateProjectSearchContext(projectId: $projectId, input: $input) {
						${searchContextFields}
					}
				}
			`,
			variables: { projectId, input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to update project search context")

	return response.data.data.updateProjectSearchContext
}

export async function deleteSearchContext(apiToken: string, id: string): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { deleteSearchContext: boolean }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation DeleteSearchContext($id: ID!) {
					deleteSearchContext(id: $id)
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to delete search context")

	return response.data.data.deleteSearchContext === true
}

export async function fetchCustomViews(apiToken: string, projectId: string | null = null): Promise<CustomView[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { customViews: CustomView[] }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				query CustomViews($projectId: UUID) {
					customViews(projectId: $projectId) {
						${customViewFields}
					}
				}
			`,
			variables: { projectId },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch custom views")

	return response.data.data.customViews ?? []
}

export async function fetchCustomView(apiToken: string, id: string): Promise<CustomView | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { customView: CustomView | null }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				query CustomView($id: UUID!) {
					customView(id: $id) {
						${customViewFields}
					}
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch custom view")

	return response.data.data.customView ?? null
}

export async function createCustomView(apiToken: string, input: CreateCustomViewInput): Promise<CustomView> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { createCustomView: CustomView }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation CreateCustomView($input: CreateCustomViewInput!) {
					createCustomView(input: $input) {
						${customViewFields}
					}
				}
			`,
			variables: {
				input: {
					...input,
					opportunityIds: input.opportunityIds?.map(String),
				},
			},
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to create custom view")

	return response.data.data.createCustomView
}

export async function updateCustomView(
	apiToken: string,
	id: string,
	input: UpdateCustomViewInput
): Promise<CustomView> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { updateCustomView: CustomView }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation UpdateCustomView($id: UUID!, $input: UpdateCustomViewInput!) {
					updateCustomView(id: $id, input: $input) {
						${customViewFields}
					}
				}
			`,
			variables: {
				id,
				input: {
					...input,
					opportunityIds: input.opportunityIds?.map(String),
				},
			},
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to update custom view")

	return response.data.data.updateCustomView
}

export async function deleteCustomView(apiToken: string, id: string): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { deleteCustomView: boolean }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation DeleteCustomView($id: UUID!) {
					deleteCustomView(id: $id)
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to delete custom view")

	return response.data.data.deleteCustomView === true
}

export async function fetchNotifications(
	apiToken: string,
	options: { limit?: number; offset?: number; activityType?: ActivityType; unreadOnly?: boolean } = {}
): Promise<PaginatedNotifications> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { notifications: PaginatedNotifications }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query Notifications($limit: Int, $offset: Int, $activityType: ActivityType, $unreadOnly: Boolean) {
					notifications(limit: $limit, offset: $offset, activityType: $activityType, unreadOnly: $unreadOnly) {
						items {
							${notificationFields}
						}
						total
					}
				}
			`,
			variables: {
				limit: options.limit,
				offset: options.offset,
				activityType: options.activityType,
				unreadOnly: options.unreadOnly,
			},
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch notifications")

	return response.data.data.notifications ?? { items: [], total: 0 }
}

export async function fetchNotificationCount(
	apiToken: string,
	activityType: ActivityType,
	unreadOnly = false
): Promise<number> {
	const notifications = await fetchNotifications(apiToken, { limit: 1, activityType, unreadOnly })

	return notifications.total
}

export async function fetchUnreadNotificationCount(apiToken: string): Promise<number> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { unreadNotificationCount: number }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query UnreadNotificationCount {
					unreadNotificationCount
				}
			`,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch unread notification count")

	return response.data.data.unreadNotificationCount ?? 0
}

type ActivityTabCounts = {
	unread: number
	projectTotal: number
	projectUnread: number
	searchTotal: number
	searchUnread: number
}

export async function fetchActivityTabCounts(apiToken: string): Promise<ActivityTabCounts> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: {
			unread: number
			projectTotal: { total: number }
			projectUnread: { total: number }
			searchTotal: { total: number }
			searchUnread: { total: number }
		}
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query ActivityTabCounts {
					unread: unreadNotificationCount
					projectTotal: notifications(limit: 1, activityType: projects) { total }
					projectUnread: notifications(limit: 1, activityType: projects, unreadOnly: true) { total }
					searchTotal: notifications(limit: 1, activityType: searches) { total }
					searchUnread: notifications(limit: 1, activityType: searches, unreadOnly: true) { total }
				}
			`,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch activity tab counts")

	const data = response.data.data

	return {
		unread: data.unread ?? 0,
		projectTotal: data.projectTotal?.total ?? 0,
		projectUnread: data.projectUnread?.total ?? 0,
		searchTotal: data.searchTotal?.total ?? 0,
		searchUnread: data.searchUnread?.total ?? 0,
	}
}

export async function markNotificationRead(apiToken: string, id: string): Promise<Notification | null> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { markNotificationRead: Notification | null }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation MarkNotificationRead($id: UUID!) {
					markNotificationRead(id: $id) {
						${notificationFields}
					}
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to mark notification as read")

	return response.data.data.markNotificationRead ?? null
}

export async function markAllAsRead(apiToken: string, activityType: ActivityType): Promise<number> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { markAllAsRead: number }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation MarkAllAsRead($activityType: ActivityType!) {
					markAllAsRead(activityType: $activityType)
				}
			`,
			variables: { activityType },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to mark notifications as read")

	return response.data.data.markAllAsRead ?? 0
}

export async function deleteNotification(apiToken: string, id: string): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { deleteNotification: boolean }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation DeleteNotification($id: UUID!) {
					deleteNotification(id: $id)
				}
			`,
			variables: { id },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to delete notification")

	return response.data.data.deleteNotification === true
}

export async function createNotification(apiToken: string, input: CreateNotificationInput): Promise<Notification> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { createNotification: Notification }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation CreateNotification($input: CreateNotificationInput!) {
					createNotification(input: $input) {
						${notificationFields}
					}
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to create notification")

	return response.data.data.createNotification
}

export async function createEmail(apiToken: string, input: CreateEmailInput): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { createEmail: boolean }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation CreateEmail($input: CreateEmailInput!) {
					createEmail(input: $input)
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to create email")

	return response.data.data.createEmail === true
}

export async function getEmailTemplates(apiToken: string): Promise<EmailTemplatePreviewTemplate[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { emailTemplates: EmailTemplatePreviewTemplate[] }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query EmailTemplates {
					emailTemplates {
						id
						name
						description
						fields {
							name
							label
							inputType
							required
							defaultValue
							helpText
							options {
								label
								value
							}
						}
					}
				}
			`,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch email template previews")

	return response.data.data.emailTemplates
}

export async function previewEmailTemplate(
	apiToken: string,
	input: PreviewEmailTemplateInput
): Promise<RenderedEmailTemplatePreview> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { previewEmailTemplate: RenderedEmailTemplatePreview }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query PreviewEmailTemplate($input: PreviewEmailTemplateInput!) {
					previewEmailTemplate(input: $input) {
						template
						subject
						previewText
						html
						text
					}
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to preview email template")

	return response.data.data.previewEmailTemplate
}

export async function sendSms(apiToken: string, input: SendSmsInput): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { sendSms: boolean }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation SendSms($input: SendSmsInput!) {
					sendSms(input: $input)
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to send SMS")

	return response.data.data.sendSms === true
}

export async function startPhoneVerification(
	apiToken: string,
	phoneNumber: string
): Promise<PhoneVerificationStartResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { startPhoneVerification: PhoneVerificationStartResult }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation StartPhoneVerification($phoneNumber: String!) {
					startPhoneVerification(phoneNumber: $phoneNumber) {
						phoneNumber
					}
				}
			`,
			variables: { phoneNumber },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to start phone verification")

	return response.data.data.startPhoneVerification
}

export async function confirmPhoneVerification(
	apiToken: string,
	phoneNumber: string,
	code: string
): Promise<PhoneVerificationResult> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { confirmPhoneVerification: PhoneVerificationResult }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation ConfirmPhoneVerification($phoneNumber: String!, $code: String!) {
					confirmPhoneVerification(phoneNumber: $phoneNumber, code: $code) {
						approved
						phoneNumber
						phoneVerifiedAt
					}
				}
			`,
			variables: { phoneNumber, code },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to confirm phone verification")

	return response.data.data.confirmPhoneVerification
}

export async function fetchNotificationPreferences(apiToken: string): Promise<NotificationPreference[]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { notificationPreferences: NotificationPreference[] }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query NotificationPreferences {
					notificationPreferences {
						${notificationPreferenceFields}
					}
				}
			`,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch notification preferences")

	return response.data.data.notificationPreferences ?? []
}

export async function setNotificationPreference(
	apiToken: string,
	input: SetNotificationPreferenceInput
): Promise<NotificationPreference> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { setNotificationPreference: NotificationPreference }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation SetNotificationPreference($input: SetNotificationPreferenceInput!) {
					setNotificationPreference(input: $input) {
						${notificationPreferenceFields}
					}
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to update notification preference")

	return response.data.data.setNotificationPreference
}

const extendedAccessFields = `
	status
	endsAt
`

export async function fetchExtendedAccess(apiToken: string): Promise<ExtendedAccess> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { currentUser: { extendedAccess: ExtendedAccess | null } }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				query ExtendedAccess {
					currentUser {
						extendedAccess {
							${extendedAccessFields}
						}
					}
				}
			`,
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to fetch extended access")

	const { extendedAccess } = response.data.data.currentUser
	if (!extendedAccess) throw new Error("Failed to fetch extended access")
	return extendedAccess
}

export async function updateCurrentUser(apiToken: string, input: { extendedAccess?: boolean }): Promise<void> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { updateCurrentUser: { id: string } }; errors?: ApiError[] }>(
		endpoint,
		{
			query: `
				mutation UpdateCurrentUser($input: UpdateCurrentUserInput!) {
					updateCurrentUser(input: $input) {
						id
					}
				}
			`,
			variables: { input },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to update current user")
}

export async function createParcelReport(
	apiToken: string,
	parcelID: string,
	content: ParcelReportContent
): Promise<[Blob, string]> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{
		data: { createParcelReport: { filename: string; contentType: string; content: string } }
		errors?: ApiError[]
	}>(
		endpoint,
		{
			query: `
				mutation CreateParcelReport($input: ParcelReportInput!) {
					createParcelReport(input: $input) {
						filename
						contentType
						content
					}
				}
			`,
			variables: {
				input: {
					parcelID,
					title: content.title,
					subtitle: content.subtitle,
					sections: content.sections.map(section => ({
						title: section.title,
						rows: section.rows.map(row => ({ label: row.label, value: row.value })),
					})),
				},
			},
		},
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to create parcel report")

	const report = response.data.data.createParcelReport
	const bytes = Uint8Array.from(atob(report.content), character => character.charCodeAt(0))
	return [new Blob([bytes], { type: report.contentType }), report.filename]
}

export async function recordParcelReportDownload(apiToken: string): Promise<void> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post<{ data: { recordParcelReportDownload: boolean }; errors?: ApiError[] }>(
		endpoint,
		{ query: `mutation RecordParcelReportDownload { recordParcelReportDownload }` },
		{ headers: { "x-api-token": apiToken } }
	)

	throwGraphQLError(response, "Failed to record parcel report download")
}

export async function moveOpportunityTarget(
	apiToken: string,
	input: {
		hubspotDealId: string
		toProjectId?: string
		fromProjectId?: string
		convertToCcv?: boolean
	}
): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation MoveOpportunityTarget($input: MoveOpportunityTargetInput!) {
					moveOpportunityTarget(input: $input)
				}
			`,
			variables: { input },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to move opportunity target")
	}

	return response.data.data.moveOpportunityTarget === true
}

export async function deleteOpportunityTarget(
	apiToken: string,
	hubspotDealId: number,
	projectId: string
): Promise<void> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation DeleteOpportunityTarget($hubspotDealId: ID!, $projectId: UUID!) {
					deleteOpportunityTarget(hubspotDealId: $hubspotDealId, projectId: $projectId)
				}
			`,
			variables: { hubspotDealId: String(hubspotDealId), projectId },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to delete opportunity target")
	}
}

export async function updateOpportunity(apiToken: string, input: UpdateOpportunityInput): Promise<boolean> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT
	const mutationInput: Record<string, unknown> = {
		projectId: input.projectId,
		opportunityId: String(input.opportunityId),
		callback: input.callback ?? true,
	}

	if (input.stage !== undefined) {
		mutationInput.stage = input.stage
	}

	if (input.priority !== undefined) {
		mutationInput.priority = input.priority
	}

	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation UpdateOpportunity($input: UpdateOpportunityInput!) {
					updateOpportunity(input: $input)
				}
			`,
			variables: { input: mutationInput },
		},
		{
			headers: {
				"x-api-token": apiToken,
			},
		}
	)

	if (response.data.errors) {
		throw new Error(response.data.errors[0]?.message ?? "Failed to update opportunity")
	}

	return response.data.data.updateOpportunity === true
}

export async function updateOpportunityEvaluation(
	apiToken: string,
	opportunityId: number,
	input: { status?: string | null; brokerNote?: string | null; clientNote?: string | null },
	projectId?: string
): Promise<{
	opportunityId: number
	status: string | null
	brokerNote: string | null
	clientNote: string | null
	updatedAt: string
}> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `
				mutation UpdateOpportunityEvaluation($input: UpdateOpportunityInput!) {
					updateOpportunityEvaluation(input: $input) {
						opportunityId
						status
						brokerNote
						clientNote
						updatedAt
					}
				}
			`,
			variables: {
				input: {
					opportunityId: String(opportunityId),
					status: input.status,
					brokerNote: input.brokerNote,
					clientNote: input.clientNote,
					projectId: projectId ?? null,
				},
			},
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) {
		const messages = response.data.errors.map((err: ApiError) => err.message)
		throw new Error(messages.join("; "))
	}

	return response.data.data.updateOpportunityEvaluation
}

export async function fetchOpportunityEvaluations(
	apiToken: string,
	projectId?: string
): Promise<
	{
		opportunityId: number
		status: string | null
		brokerNote: string | null
		clientNote: string | null
		stage: OpportunityStage | null
		priority: OpportunityPriority
	}[]
> {
	const endpoint = import.meta.env.VITE_GRAPHQL_API_ENDPOINT

	const response = await axios.post(
		endpoint,
		{
			query: `
				query OpportunityEvaluations($projectId: ID) {
					opportunityEvaluations(projectId: $projectId) {
						opportunityId
						status
						brokerNote
						clientNote
						stage
						priority
					}
				}
			`,
			variables: { projectId: projectId ?? null },
		},
		{ headers: { "x-api-token": apiToken } }
	)

	if (response.data.errors) {
		const messages = response.data.errors.map((err: ApiError) => err.message)
		throw new Error(messages.join("; "))
	}

	return response.data.data.opportunityEvaluations.map(
		(e: {
			opportunityId: string
			status: string | null
			brokerNote: string | null
			clientNote: string | null
			stage: OpportunityStage | null
			priority: OpportunityPriority
		}) => ({
			opportunityId: Number(e.opportunityId),
			status: e.status,
			brokerNote: e.brokerNote,
			clientNote: e.clientNote,
			stage: e.stage,
			priority: e.priority,
		})
	)
}

export async function askRegulatoryAssistant(
	apiToken: string,
	question: string,
	parcelId: string | null,
	signal?: AbortSignal
): Promise<RegulatoryAssistantResponse> {
	const response = await fetch(import.meta.env.VITE_GRAPHQL_API_ENDPOINT, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"x-api-token": apiToken,
		},
		body: JSON.stringify({
			query: `query AskLandGpt($input: LandGptInput!) {
				askLandGpt(input: $input)
			}`,
			variables: { input: { question, parcelId } },
		}),
		signal,
	})

	if (!response.ok) {
		throw new Error(`Regulatory assistant request failed with status ${response.status}`)
	}

	const body = (await response.json()) as { data?: { askLandGpt?: unknown }; errors?: ApiError[] }

	if (body.errors?.length) {
		throw new Error(body.errors.map(error => error.message).join("; "))
	}

	return mapRegulatoryAssistantResponse(body.data?.askLandGpt)
}
